import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import Card from '../components/Card'
import Button from '../components/Button'
import LoadingSpinner from '../components/LoadingSpinner'
import api from '../services/api'
import { getToken, getUser } from '../utils/userData'
import { 
  getMediaUrl, 
  isExternalVideoUrl, 
  getVideoEmbedUrl, 
  getYouTubeThumbnail,
  getMediaDisplayName 
} from '../utils/mediaHelpers'
import { Link2 } from 'lucide-react'
import { initMetaPixel, trackInitiateCheckout, trackPurchase } from '../utils/metaPixel'
import { trackEvent } from '../utils/mixpanel'
import {
  needsExtendedForm2,
  needsFamilyIntakeForm,
  needsUnmaskingHalfdayApr2026Form,
  needsAWayToMoveForm,
  getAvailableAWayToMoveSlotOptions,
  UNMASKING_SESSION_OPTIONS,
  UNMASKING_GENDER_OPTIONS,
} from '../utils/eventRegistrationFormTypes'
import ExtendedForm2PaymentSection from '../components/events/ExtendedForm2PaymentSection'
import AWayToMoveSlotPicker from '../components/events/AWayToMoveSlotPicker'
import { parseFee } from '../utils/parseFee'

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000)
    return () => clearTimeout(timer)
  }, [onClose])

  const bgColor = type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : 'bg-primary-600'
  const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] ${bgColor} text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3`}
    >
      <span className="text-lg font-bold">{icon}</span>
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:bg-white/20 rounded-full p-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </motion.div>
  )
}

// Confirmation modal component
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Confirm', cancelText = 'Cancel' }) => {
  if (!isOpen) return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={onCancel}>{cancelText}</Button>
          <Button variant="primary" onClick={onConfirm}>{confirmText}</Button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// Small media thumbnail strip - clicking changes the displayed media
const MediaThumbnails = ({ media, selectedIndex, onSelect }) => {
  if (!media || media.length === 0) return null

  return (
    <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4">
      {media.map((item, index) => {
        const isOriginalCover = item.isOriginalCover
        const mediaUrl = isOriginalCover 
          ? (item.url.startsWith('http') ? item.url : `${import.meta.env.VITE_API_URL || ''}${item.url}`)
          : getMediaUrl(item.url)
        const isSelected = selectedIndex === index
        const isExternal = isExternalVideoUrl(item.url)
        const thumbnailUrl = isExternal ? getYouTubeThumbnail(item.url) : null

        // For PDFs, show as link instead of thumbnail
        if (item.type === 'pdf') {
          return (
            <a
              key={item.id}
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-red-50 flex items-center justify-center hover:bg-red-100 transition-colors border border-gray-200"
              title={getMediaDisplayName(item)}
            >
              <span className="text-sm">📄</span>
            </a>
          )
        }

        return (
          <button
            key={item.id}
            onClick={() => onSelect(index)}
            className={`flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-gray-100 transition-all ${
              isSelected 
                ? 'ring-2 ring-primary-500 ring-offset-1' 
                : 'hover:opacity-80 border border-gray-200'
            }`}
            title={isOriginalCover ? 'Original cover' : (item.caption || '')}
          >
            {item.type === 'image' && (
              <img
                src={mediaUrl}
                alt={item.caption || 'Thumbnail'}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = '/uploads/events/default-event.jpg' }}
              />
            )}
            {item.type === 'video' && (
              <div className="w-full h-full relative">
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt="Video"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                    <span className="text-xs">🎬</span>
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

//TODO: better way to handle this
const COUNTRY_CODES = [
  { code: '+91', label: '🇮🇳 +91' },
  { code: '+1', label: '🇺🇸 +1' },
  { code: '+44', label: '🇬🇧 +44' },
  { code: '+971', label: '🇦🇪 +971' },
  { code: '+65', label: '🇸🇬 +65' },
  { code: '+61', label: '🇦🇺 +61' },
  { code: '+49', label: '🇩🇪 +49' },
  { code: '+33', label: '🇫🇷 +33' },
  { code: '+81', label: '🇯🇵 +81' },
  { code: '+86', label: '🇨🇳 +86' },
  { code: '+966', label: '🇸🇦 +966' },
  { code: '+974', label: '🇶🇦 +974' },
  { code: '+60', label: '🇲🇾 +60' },
  { code: '+62', label: '🇮🇩 +62' },
  { code: '+27', label: '🇿🇦 +27' },
  { code: '+234', label: '🇳🇬 +234' },
  { code: '+254', label: '🇰🇪 +254' },
]

const PRIMARY_GOAL_OPTIONS = [
  'I lead a high stress life and think fixing my breathing can help',
  'I struggle with sleep and think that breathing better can help',
  'I suffer from anxiety and depression and would like to know how breathing better can help',
  'I want to understand what breathing right every day looks like so I fix my breathing',
  "I'm just curious",
]

const HEARD_ABOUT_OPTIONS = [
  'Social Media (LinkedIn, Facebook, etc.)',
  'From ads',
  'From a friend',
  'Whatsapp Group',
]

const needsExtendedForm = (event) => event?.formType === 'extended-workshop'
const EXTENDED_FORM_PAYMENT_TEXT = 'Please transfer Rs. 99 to the following QR code'

const getEventDetailPageUrl = (slug) => {
  if (!slug) return ''
  const path = `/events/${encodeURIComponent(slug)}`
  if (typeof window === 'undefined') return path
  return `${window.location.origin}${path}`
}

const isGatewayPaid = (event) =>
  Boolean(event?.paymentGatewayEnabled && parseFee(event?.fee) > 0)

const Events = () => {
  const navigate = useNavigate()
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [filterFormat, setFilterFormat] = useState('all')
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [registering, setRegistering] = useState(false)
  const [userRegistrations, setUserRegistrations] = useState([])
  const [toast, setToast] = useState(null)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, eventId: null })
  const [eventDetailLinkCopied, setEventDetailLinkCopied] = useState(false)
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0)
  const [guestName, setGuestName] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [guestRegistered, setGuestRegistered] = useState(false)

  // Extended form state
  const [guestCountryCode, setGuestCountryCode] = useState('+91')
  const [guestJobTitle, setGuestJobTitle] = useState('')
  const [guestOrganization, setGuestOrganization] = useState('')
  const [guestPrimaryGoals, setGuestPrimaryGoals] = useState([])
  const [guestOtherGoal, setGuestOtherGoal] = useState('')
  const [guestHeardAbout, setGuestHeardAbout] = useState('')
  const [guestOtherHeardAbout, setGuestOtherHeardAbout] = useState('')
  const [guestTransactionId, setGuestTransactionId] = useState('')

  const [guestChildGender, setGuestChildGender] = useState('')
  const [guestChildGenderOther, setGuestChildGenderOther] = useState('')
  const [guestChildAge, setGuestChildAge] = useState('')
  const [guestAboutYourself, setGuestAboutYourself] = useState('')
  const [guestSessionHope, setGuestSessionHope] = useState('')
  const [guestSharedQuestions, setGuestSharedQuestions] = useState('')

  const [guestAge, setGuestAge] = useState('')
  const [guestGender, setGuestGender] = useState('')
  const [guestGenderOther, setGuestGenderOther] = useState('')
  const [guestCompany, setGuestCompany] = useState('')
  const [guestSessionSelection, setGuestSessionSelection] = useState('')
  const [guestFacilitatorQueries, setGuestFacilitatorQueries] = useState('')
  const [guestSlotSelection, setGuestSlotSelection] = useState('')

  const isLoggedIn = !!getToken()
  const currentUser = getUser()

  // Capture eventId from URL on mount (e.g. after login redirect)
  const pendingEventIdRef = useRef(
    new URLSearchParams(window.location.search).get('eventId')
  )

  useEffect(() => {
    initMetaPixel()
    trackEvent('Page Viewed', { page: 'Events' })
  }, [])

  const showToast = (message, type = 'info') => {
    setToast({ message, type })
  }

  const hideToast = () => {
    setToast(null)
  }

  useEffect(() => {
    fetchEvents()
    if (isLoggedIn) {
      fetchUserRegistrations()
    }
  }, [isLoggedIn])

  // Auto-open event modal once events are loaded
  useEffect(() => {
    const eventId = pendingEventIdRef.current
    if (eventId && events.length > 0) {
      const event = events.find(e => String(e.id) === eventId)
      if (event) {
        setSelectedEvent(event)
        setSelectedMediaIndex(0)
        setGuestName(isLoggedIn && currentUser?.name ? currentUser.name : '')
        setGuestEmail(isLoggedIn && currentUser?.email ? currentUser.email : '')
        setGuestPhone(isLoggedIn && currentUser?.phone ? currentUser.phone : '')
        setGuestCountryCode('+91')
        setGuestJobTitle('')
        setGuestOrganization('')
        setGuestPrimaryGoals([])
        setGuestOtherGoal('')
        setGuestHeardAbout('')
        setGuestOtherHeardAbout('')
        setGuestTransactionId('')
        setGuestChildGender('')
        setGuestChildGenderOther('')
        setGuestChildAge('')
        setGuestAboutYourself('')
        setGuestSessionHope('')
        setGuestSharedQuestions('')
        setGuestAge('')
        setGuestGender('')
        setGuestGenderOther('')
        setGuestCompany('')
        setGuestSessionSelection('')
        setGuestFacilitatorQueries('')
        setGuestSlotSelection('')
        setGuestRegistered(false)
      }
      // Clear so it doesn't re-trigger
      pendingEventIdRef.current = null
      // Clean up the URL
      window.history.replaceState({}, '', '/events')
    }
  }, [events, isLoggedIn, currentUser?.name, currentUser?.email, currentUser?.phone])

  useEffect(() => {
    if (!selectedEvent) setEventDetailLinkCopied(false)
  }, [selectedEvent])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/api/events')
      setEvents(response.data.data)
    } catch (err) {
      console.error('Error fetching events:', err)
      setError('Failed to load events. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const fetchUserRegistrations = async () => {
    try {
      const response = await api.get('/api/events/my-registrations')
      setUserRegistrations(response.data.data.map(r => r.event.id))
    } catch (err) {
      console.error('Error fetching registrations:', err)
    }
  }

  const handleCopyEventDetailPageLink = async () => {
    if (!selectedEvent?.slug) return
    const url = getEventDetailPageUrl(selectedEvent.slug)
    try {
      await navigator.clipboard.writeText(url)
      setEventDetailLinkCopied(true)
      setTimeout(() => setEventDetailLinkCopied(false), 2000)
    } catch {
      alert('Could not copy link. Copy manually: ' + url)
    }
  }

  const buildFamilyIntakeFormData = () => ({
    childGender:
      guestChildGender === 'Other'
        ? `Other: ${guestChildGenderOther.trim()}`
        : guestChildGender.trim(),
    childAge: guestChildAge.trim(),
    aboutYourself: guestAboutYourself.trim(),
    sessionHope: guestSessionHope.trim(),
    questions: guestSharedQuestions.trim(),
  })

  const validateFamilyIntakeForSubmit = () => {
    if (!guestPhone.trim()) {
      showToast('Please enter your phone number', 'error')
      return false
    }
    if (guestChildGender !== 'Male' && guestChildGender !== 'Other') {
      showToast("Please select your child's gender", 'error')
      return false
    }
    if (guestChildGender === 'Other' && !guestChildGenderOther.trim()) {
      showToast('Please specify for Other under child\'s gender', 'error')
      return false
    }
    if (!guestChildAge.trim()) {
      showToast("Please enter your child's age", 'error')
      return false
    }
    if (!guestAboutYourself.trim()) {
      showToast('Please tell us a little about yourself', 'error')
      return false
    }
    if (!guestSessionHope.trim()) {
      showToast('Please share what you hope to get out of this session', 'error')
      return false
    }
    if (!guestSharedQuestions.trim()) {
      showToast('Please complete the questions field (or write "None")', 'error')
      return false
    }
    return true
  }

  const buildUnmaskingHalfdayFormData = () => ({
    age: guestAge.trim(),
    gender:
      guestGender === 'Other'
        ? `Other: ${guestGenderOther.trim()}`
        : guestGender.trim(),
    company: guestCompany.trim(),
    sessionSelection: guestSessionSelection.trim(),
    transactionId: guestTransactionId.trim(),
    ...(guestFacilitatorQueries.trim()
      ? { facilitatorQueries: guestFacilitatorQueries.trim() }
      : {}),
  })

  const buildAWayToMoveFormData = () => ({
    slotSelection: guestSlotSelection.trim(),
  })

  const validateAWayToMoveForSubmit = () => {
    if (getAvailableAWayToMoveSlotOptions().length === 0) {
      showToast('No trial slots are available right now', 'error')
      return false
    }
    if (!guestSlotSelection.trim()) {
      showToast('Please select a trial slot', 'error')
      return false
    }
    return true
  }

  const validateUnmaskingHalfdayForSubmit = () => {
    if (!guestAge.trim()) {
      showToast('Please enter your age', 'error')
      return false
    }
    if (!guestGender) {
      showToast('Please select your gender', 'error')
      return false
    }
    if (guestGender === 'Other' && !guestGenderOther.trim()) {
      showToast('Please specify your gender', 'error')
      return false
    }
    if (!guestCompany.trim()) {
      showToast('Please enter your company', 'error')
      return false
    }
    if (!guestSessionSelection.trim()) {
      showToast('Please select a session option', 'error')
      return false
    }
    if (!guestTransactionId.trim()) {
      showToast('Please enter your payment transaction ID', 'error')
      return false
    }
    return true
  }

  const buildExtendedFormData = () => {
    const goals = [...guestPrimaryGoals]
    if (guestPrimaryGoals.includes('Other') && guestOtherGoal.trim()) {
      goals[goals.indexOf('Other')] = `Other: ${guestOtherGoal.trim()}`
    } else {
      const otherIdx = goals.indexOf('Other')
      if (otherIdx !== -1) goals.splice(otherIdx, 1)
    }
    return {
      jobTitle: guestJobTitle.trim() || undefined,
      organization: guestOrganization.trim() || undefined,
      primaryGoals: goals.length > 0 ? goals : undefined,
      heardAbout: guestHeardAbout === 'Other'
        ? `Other: ${guestOtherHeardAbout.trim()}`
        : guestHeardAbout || undefined,
      transactionId: guestTransactionId.trim() || undefined,
    }
  }

  const handleRegister = async (eventId) => {
    if (!isLoggedIn) {
      showToast('Please log in to register for events', 'info')
      return
    }

    const event = events.find(e => e.id === eventId) || selectedEvent
    if (needsExtendedForm(event)) {
      if (!guestPhone.trim()) {
        showToast('Please enter your phone number', 'error')
        return
      }
      if (!guestHeardAbout) {
        showToast('Please select how you heard about this workshop', 'error')
        return
      }
      if (!guestTransactionId.trim()) {
        showToast('Please enter your payment transaction ID', 'error')
        return
      }
    }
    if (needsExtendedForm2(event)) {
      if (!guestTransactionId.trim()) {
        showToast('Please enter your payment transaction ID', 'error')
        return
      }
    }
    if (needsUnmaskingHalfdayApr2026Form(event)) {
      if (!guestPhone.trim()) {
        showToast('Please enter your phone number', 'error')
        return
      }
      if (!validateUnmaskingHalfdayForSubmit()) return
    }
    if (needsFamilyIntakeForm(event) && !validateFamilyIntakeForSubmit()) {
      return
    }
    if (needsAWayToMoveForm(event) && !validateAWayToMoveForSubmit()) {
      return
    }

    setRegistering(true)
    try {
      const body = {}
      if (needsExtendedForm(event)) {
        body.formData = {
          ...buildExtendedFormData(),
          phone: `${guestCountryCode} ${guestPhone.trim()}`,
        }
      }
      if (needsExtendedForm2(event)) {
        body.formData = { transactionId: guestTransactionId.trim() }
      }
      if (needsUnmaskingHalfdayApr2026Form(event)) {
        body.formData = buildUnmaskingHalfdayFormData()
      }
      if (needsFamilyIntakeForm(event)) {
        body.formData = buildFamilyIntakeFormData()
      }
      if (needsAWayToMoveForm(event)) {
        body.formData = buildAWayToMoveFormData()
      }
      const response = await api.post(`/api/events/${eventId}/register`, body)
      const registrationId = response.data.data?.id
      const registeredEvent = events.find(e => e.id === eventId)
      if (registeredEvent) trackPurchase(registeredEvent, registrationId)
      if (registeredEvent) {
        trackEvent('Purchase', {
          transaction_id: String(registrationId),
          content_name: registeredEvent.name,
          currency: 'INR',
        })
        trackEvent('Conversion', { 'Conversion Type': 'event_registration' })
      }
      navigate('/events/registration-success', {
        state: {
          eventName: registeredEvent?.name,
          eventSlug: registeredEvent?.slug,
          isPaid: false,
          registrationId,
        },
      })
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to register', 'error')
    } finally {
      setRegistering(false)
    }
  }

  const handleGuestRegister = async (e) => {
    e.preventDefault()
    if (!guestName.trim() || !guestEmail.trim() || !guestPhone.trim()) {
      showToast('Please fill in all required fields', 'error')
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(guestEmail.trim())) {
      showToast('Please enter a valid email address', 'error')
      return
    }
    if (needsExtendedForm(selectedEvent) && !guestHeardAbout) {
      showToast('Please select how you heard about this workshop', 'error')
      return
    }
    if (needsExtendedForm(selectedEvent) && !guestTransactionId.trim()) {
      showToast('Please enter your payment transaction ID', 'error')
      return
    }
    if (needsExtendedForm2(selectedEvent) && !guestTransactionId.trim()) {
      showToast('Please enter your payment transaction ID', 'error')
      return
    }
    if (needsUnmaskingHalfdayApr2026Form(selectedEvent) && !validateUnmaskingHalfdayForSubmit()) {
      return
    }
    if (needsFamilyIntakeForm(selectedEvent) && !validateFamilyIntakeForSubmit()) {
      return
    }
    if (needsAWayToMoveForm(selectedEvent) && !validateAWayToMoveForSubmit()) {
      return
    }
    setRegistering(true)
    try {
      const phoneValue = needsExtendedForm(selectedEvent)
        ? `${guestCountryCode} ${guestPhone.trim()}`
        : guestPhone.trim()
      const body = {
        name: guestName.trim(),
        email: guestEmail.trim(),
        phone: phoneValue,
      }
      if (needsExtendedForm(selectedEvent)) {
        body.formData = buildExtendedFormData()
      }
      if (needsExtendedForm2(selectedEvent)) {
        body.formData = { transactionId: guestTransactionId.trim() }
      }
      if (needsUnmaskingHalfdayApr2026Form(selectedEvent)) {
        body.formData = buildUnmaskingHalfdayFormData()
      }
      if (needsFamilyIntakeForm(selectedEvent)) {
        body.formData = buildFamilyIntakeFormData()
      }
      if (needsAWayToMoveForm(selectedEvent)) {
        body.formData = buildAWayToMoveFormData()
      }
      const response = await api.post(`/api/events/${selectedEvent.id}/guest-register`, body)
      const registrationId = response.data.data?.id
      trackPurchase(selectedEvent, registrationId)
      trackEvent('Purchase', {
        transaction_id: String(registrationId),
        content_name: selectedEvent.name,
        currency: 'INR',
      })
      trackEvent('Conversion', { 'Conversion Type': 'event_registration' })
      navigate('/events/registration-success', {
        state: {
          eventName: selectedEvent.name,
          eventSlug: selectedEvent.slug,
          isPaid: false,
          registrationId,
        },
      })
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to register', 'error')
    } finally {
      setRegistering(false)
    }
  }

  const handleCancelRegistration = async (eventId) => {
    setConfirmModal({ isOpen: true, eventId })
  }

  const confirmCancelRegistration = async () => {
    const eventId = confirmModal.eventId
    setConfirmModal({ isOpen: false, eventId: null })

    setRegistering(true)
    try {
      await api.delete(`/api/events/${eventId}/register`)
      setUserRegistrations(prev => prev.filter(id => id !== eventId))
      // Update event in state
      setEvents(prev => prev.map(e => 
        e.id === eventId ? { ...e, registrationCount: Math.max((e.registrationCount || 1) - 1, 0) } : e
      ))
      // Also update selected event if it's the same
      setSelectedEvent(prev => prev?.id === eventId ? { ...prev, registrationCount: Math.max((prev.registrationCount || 1) - 1, 0) } : prev)
      showToast('Registration cancelled successfully', 'success')
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to cancel registration', 'error')
    } finally {
      setRegistering(false)
    }
  }

  const isRegistered = (eventId) => userRegistrations.includes(eventId)

  const filteredEvents = filterFormat === 'all' 
    ? events 
    : events.filter(event => event.format.toLowerCase() === filterFormat.toLowerCase())

  const getFormatBadgeColor = (format) => {
    switch (format.toLowerCase()) {
      case 'online':
        return 'bg-blue-100 text-blue-800'
      case 'in-person':
        return 'bg-green-100 text-green-800'
      case 'hybrid':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateRange = (startDateStr, endDateStr) => {
    const start = new Date(startDateStr)
    if (!endDateStr) return formatDate(startDateStr)
    
    const end = new Date(endDateStr)
    // Same day — just show the single date
    if (start.toDateString() === end.toDateString()) {
      return formatDate(startDateStr)
    }

    // Multi-day event
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
    if (sameMonth) {
      // e.g. "Sun 15 - Mon 16 February, 2026"
      const startPart = start.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' })
      const endPart = end.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
      return `${startPart} - ${endPart}`
    }
    // Different months — e.g. "Sun 28 February - Mon 1 March, 2026"
    const startPart = start.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long' })
    const endPart = end.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
    return `${startPart} - ${endPart}`
  }

  const getEventImage = (event) => {
    // If the event has an image, use it
    if (event.image) {
      if (event.image.startsWith('http')) return event.image
      return `${import.meta.env.VITE_API_URL || ''}${event.image}`
    }

    // If the event has media, use the first image from media gallery
    if (event.media && event.media.length > 0)   {
      const firstImageMedia = event.media.find(m => m.type === 'image')
      if (firstImageMedia) {
        return getMediaUrl(firstImageMedia.url)
      }
    }
    // Otherwise use the default event image
    return `${import.meta.env.VITE_API_URL}/uploads/events/default-event.jpg`
  }

  const getFacilitatorNames = (event) => {
    const names = []
    if (event.facilitators && event.facilitators.length > 0) {
      names.push(...event.facilitators.map(f => f.name))
    }
    if (event.externalFacilitators && event.externalFacilitators.length > 0) {
      names.push(...event.externalFacilitators)
    }
    return names
  }

  const renderFacilitatorLinks = (event, stopPropagation = false) => {
    const elements = []
    
    // Add internal facilitators (with potential slugs)
    if (event.facilitators && event.facilitators.length > 0) {
      event.facilitators.forEach((f, index) => {
        if (index > 0 || elements.length > 0) {
          elements.push(<span key={`sep-${index}`}>, </span>)
        }
        if (f.slug) {
          elements.push(
            <Link 
              key={`fac-${f.id || index}`}
              to={`/facilitator/${f.slug}`}
              className="text-primary-600 underline font-semibold hover:text-primary-700 transition-colors"
              onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
            >
              {f.name}
            </Link>
          )
        } else {
          elements.push(<span key={`fac-${f.id || index}`}>{f.name}</span>)
        }
      })
    }
    
    // Add external facilitators (no links)
    if (event.externalFacilitators && event.externalFacilitators.length > 0) {
      event.externalFacilitators.forEach((name, index) => {
        if (elements.length > 0) {
          elements.push(<span key={`ext-sep-${index}`}>, </span>)
        }
        elements.push(<span key={`ext-${index}`}>{name}</span>)
      })
    }
    
    return elements
  }

  const getLocationText = (event) => {
    if (event.space) {
      return event.space.name + (event.space.city ? `, ${event.space.city}` : '')
    }
    return event.location || (event.format === 'online' ? 'Online' : 'Location TBA')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={fetchEvents}>Try Again</Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      {/* Toast Notification */}
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <AnimatePresence>
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title="Cancel Registration"
          message="Are you sure you want to cancel your registration for this event?"
          confirmText="Yes, Cancel"
          cancelText="Keep Registration"
          onConfirm={confirmCancelRegistration}
          onCancel={() => setConfirmModal({ isOpen: false, eventId: null })}
        />
      </AnimatePresence>

      {/* Header Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-5 md:py-6">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              Events
            </h1>
            <p className="text-sm md:text-base text-white/90 mb-4">
              Discover transformative experiences in Bangalore and online. Connect, heal, and grow with our curated wellness events.
            </p>
            
            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setFilterFormat('all')}
                className={`px-4 py-1.5 text-sm rounded-full font-semibold transition-all ${
                  filterFormat === 'all'
                    ? 'bg-white text-primary-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                All Events ({events.length})
              </button>
              <button
                onClick={() => setFilterFormat('in-person')}
                className={`px-4 py-1.5 text-sm rounded-full font-semibold transition-all ${
                  filterFormat === 'in-person'
                    ? 'bg-white text-green-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                In-Person
              </button>
              <button
                onClick={() => setFilterFormat('online')}
                className={`px-4 py-1.5 text-sm rounded-full font-semibold transition-all ${
                  filterFormat === 'online'
                    ? 'bg-white text-blue-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                Online
              </button>
              <button
                onClick={() => setFilterFormat('hybrid')}
                className={`px-4 py-1.5 text-sm rounded-full font-semibold transition-all ${
                  filterFormat === 'hybrid'
                    ? 'bg-white text-purple-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                Hybrid
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="pt-2 pb-10">
        <div className="container mx-auto px-4">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">📅</div>
              <p className="text-2xl text-gray-600">No upcoming events found.</p>
              <p className="text-gray-500 mt-2">Check back soon for new events!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 lg:grid-cols-4 lg:gap-10 gap-4">
              {filteredEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full cursor-pointer hover:shadow-2xl transition-all bg-white overflow-hidden group p-0">
                    <div onClick={() => { setSelectedEvent(event); setSelectedMediaIndex(0); setGuestName(isLoggedIn && currentUser?.name ? currentUser.name : ''); setGuestEmail(isLoggedIn && currentUser?.email ? currentUser.email : ''); setGuestPhone(isLoggedIn && currentUser?.phone ? currentUser.phone : ''); setGuestCountryCode('+91'); setGuestJobTitle(''); setGuestOrganization(''); setGuestPrimaryGoals([]); setGuestOtherGoal(''); setGuestHeardAbout(''); setGuestOtherHeardAbout(''); setGuestTransactionId(''); setGuestChildGender(''); setGuestChildGenderOther(''); setGuestChildAge(''); setGuestAboutYourself(''); setGuestSessionHope(''); setGuestSharedQuestions(''); setGuestAge(''); setGuestGender(''); setGuestGenderOther(''); setGuestCompany(''); setGuestSessionSelection(''); setGuestFacilitatorQueries(''); setGuestSlotSelection(''); setGuestRegistered(false); trackInitiateCheckout(event); trackEvent('Event Viewed', { event_id: event.id, event_name: event.name, event_format: event.format, event_fee: event.fee || 'N/A', event_date: event.eventDate }); }}>
                      {/* Event Image */}
                      <div className="aspect-[4/5] overflow-hidden bg-cream">
                        <img 
                          src={getEventImage(event)} 
                          alt={event.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.target.src = '/uploads/events/default-event.jpg' }}
                        />
                      </div>
                      
                      <div className="p-2 pl-4">
                        {/* Format Badge */}
                        <div className="mb-2 flex flex-wrap gap-2">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getFormatBadgeColor(event.format)}`}>
                            {event.format}
                          </span>
                          {event.fee?.toLowerCase() === 'free' && (
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                              FREE
                            </span>
                          )}
                          {event.registrationOpen && event.maxAttendees && (
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                              {event.maxAttendees} spots
                            </span>
                          )}
                        </div>

                        {/* Event Name */}
                        <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">
                          {event.name}
                        </h3>

                        {/* Date and Time */}
                        <div className="flex items-center gap-2 text-gray-600 mb-1.5">
                          <span className="text-lg w-5 flex-shrink-0 text-center">📅</span>
                          <div>
                            <p className="font-semibold">{formatDateRange(event.eventDate, event.endDate)}</p>
                            <p className="text-sm">
                              {formatTime(event.eventDate)}
                              {event.endDate && ` - ${formatTime(event.endDate)}`}
                            </p>
                          </div>
                        </div>

                        {/* Location */}
                        <div className="flex items-center gap-2 text-gray-600 mb-1.5">
                          <span className="text-lg w-5 flex-shrink-0 text-center">📍</span>
                          <p className="text-sm">{getLocationText(event)}</p>
                        </div>

                        {/* Facilitators */}
                        {getFacilitatorNames(event).length > 0 && (
                          <div className="flex items-center gap-2 text-gray-600 mb-1.5">
                            <span className="text-lg w-5 flex-shrink-0 text-center">👤</span>
                            <p className="text-sm">{renderFacilitatorLinks(event, true)}</p>
                          </div>
                        )}

                        {/* Short Description */}
                        <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                          {event.shortDescription}
                        </p>

                        {/* Fee */}
                        {event.fee && event.fee.toLowerCase() !== 'free' && (
                          <div className="flex items-center gap-2 mb-3">
                            <span className=" font-semibold text-primary-600">{event.fee}</span>
                          </div>
                        )}

                        {/* View Details Button */}
                        <Button 
                          variant="primary" 
                          className="w-full"
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6 rounded-t-2xl">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="mb-3 flex flex-wrap gap-2">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-white/20`}>
                        {selectedEvent.format}
                      </span>
                      {selectedEvent.fee?.toLowerCase() === 'free' && (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-400 text-yellow-900">
                          FREE
                        </span>
                      )}
                    </div>
                    <h2 className="text-3xl font-bold mb-2">{selectedEvent.name}</h2>
                    {selectedEvent.fee && selectedEvent.fee.toLowerCase() !== 'free' && (
                      <p className="text-2xl font-bold">{selectedEvent.fee}</p>
                    )}
                    {selectedEvent.slug && (
                      <button
                        type="button"
                        onClick={handleCopyEventDetailPageLink}
                        className="mt-3 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-white/15 border border-white/30 hover:bg-white/25 transition-colors w-full sm:w-auto sm:justify-start"
                      >
                        <Link2 className="w-4 h-4 flex-shrink-0" aria-hidden />
                        {eventDetailLinkCopied ? 'Copied!' : 'Copy event page link'}
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedEvent(null)}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {/* Main Display Image/Video */}
                {(() => {
                  // Build combined media array: cover image first, then event media
                  const hasEventMedia = selectedEvent.media && selectedEvent.media.length > 0
                  const originalCover = selectedEvent.image
                  
                  // Cover image first, then event media
                  let allMedia = []
                  if (originalCover) {
                    allMedia.push({ id: 'cover', type: 'image', url: originalCover, isOriginalCover: true })
                  }
                  if (hasEventMedia) {
                    allMedia = [...allMedia, ...(selectedEvent.media || [])]
                  }
                  
                  const currentMedia = allMedia[selectedMediaIndex] || allMedia[0]
                  const currentUrl = currentMedia?.isOriginalCover 
                    ? (originalCover.startsWith('http') ? originalCover : `${import.meta.env.VITE_API_URL || ''}${originalCover}`)
                    : (currentMedia ? getMediaUrl(currentMedia.url) : `${import.meta.env.VITE_API_URL}/uploads/events/default-event.jpg`)
                  
                  const isVideo = currentMedia?.type === 'video'
                  const isExternal = isVideo && isExternalVideoUrl(currentMedia?.url)
                  const embedUrl = isExternal ? getVideoEmbedUrl(currentMedia?.url) : null

                  return (
                    <>
                      <div className="w-full flex justify-center rounded-xl bg-gray-200 mb-3">
                        {isVideo && isExternal && embedUrl ? (
                          <div className="w-full aspect-video">
                            <iframe
                              src={embedUrl}
                              className="w-full h-full rounded-xl"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title="Video"
                            />
                          </div>
                        ) : isVideo && !isExternal ? (
                          <video
                            src={currentUrl}
                            controls
                            className="max-h-[400px] w-auto rounded-xl"
                          >
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <img 
                            src={currentUrl} 
                            alt={selectedEvent.name}
                            className="max-h-[400px] w-auto rounded-xl object-contain"
                            onError={(e) => { e.target.src = '/events-img/default-event.jpg' }}
                          />
                        )}
                      </div>

                      {/* Media Thumbnails - only show if there's more than 1 item */}
                      {allMedia.length > 1 && (
                        <MediaThumbnails 
                          media={allMedia}
                          selectedIndex={selectedMediaIndex}
                          onSelect={setSelectedMediaIndex}
                        />
                      )}
                    </>
                  )
                })()}

                {/* Date, Time, Location */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">📅</span>
                    <div>
                      <p className="font-semibold text-lg text-gray-900">{formatDateRange(selectedEvent.eventDate, selectedEvent.endDate)}</p>
                      <p className="text-gray-600">
                        {formatTime(selectedEvent.eventDate)}
                        {selectedEvent.endDate && ` - ${formatTime(selectedEvent.endDate)}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="text-2xl">📍</span>
                    <div>
                      <p className="text-gray-900">{getLocationText(selectedEvent)}</p>
                      {(selectedEvent.venueLink || selectedEvent.space?.googleMapsUrl) && (
                        <a 
                          href={selectedEvent.venueLink || selectedEvent.space?.googleMapsUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline text-sm"
                        >
                          View on Map →
                        </a>
                      )}
                    </div>
                  </div>

                  {getFacilitatorNames(selectedEvent).length > 0 && (
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">👤</span>
                      <div>
                        <p className="font-semibold text-gray-900">Facilitators</p>
                        <p className="text-gray-600">{renderFacilitatorLinks(selectedEvent)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Short Description */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">About This Event</h3>
                  <p className="text-gray-700 leading-relaxed font-semibold">
                    {selectedEvent.shortDescription}
                  </p>
                </div>

                {/* Detailed Description */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Details</h3>
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {selectedEvent.detailedDescription}
                  </div>
                </div>

                {/* Registration Section */}
                <div className="bg-primary-50 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Registration</h3>
                  
                  {/* Capacity info */}
                  {selectedEvent.maxAttendees && (
                    <p className="text-gray-700 mb-3">
                      {selectedEvent.maxAttendees} spots
                    </p>
                  )}

                  {selectedEvent.registrationInfo && (
                    <p className="text-gray-700 mb-4">{selectedEvent.registrationInfo}</p>
                  )}
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Platform Registration */}
                    {selectedEvent.registrationOpen && (
                      isRegistered(selectedEvent.id) ? (
                        <Button 
                          variant="secondary" 
                          className="flex-1"
                          onClick={() => handleCancelRegistration(selectedEvent.id)}
                          disabled={registering}
                        >
                          {registering ? 'Processing...' : '✓ Enquired - Click to Cancel'}
                        </Button>
                      ) : guestRegistered ? (
                        <div className="flex-1 text-center py-3 bg-green-50 rounded-xl border border-green-200">
                          <p className="text-green-700 font-semibold">Successfully Registered!</p>
                        </div>
                      ) : isGatewayPaid(selectedEvent) ? (
                        <form
                          className="flex-1 space-y-3"
                          onSubmit={(e) => {
                            e.preventDefault()
                            if (!guestName.trim() || !guestEmail.trim() || !guestPhone.trim()) {
                              showToast('Please fill in all required fields', 'error')
                              return
                            }
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                            if (!emailRegex.test(guestEmail.trim())) {
                              showToast('Please enter a valid email address', 'error')
                              return
                            }
                            navigate(`/events/${selectedEvent.slug}`, {
                              state: {
                                prefill: {
                                  name: guestName.trim(),
                                  email: guestEmail.trim(),
                                  phone: guestPhone.trim(),
                                },
                              },
                            })
                          }}
                        >
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              placeholder="Your Name"
                              value={guestName}
                              onChange={(e) => setGuestName(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.name}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              placeholder="Email Address"
                              value={guestEmail}
                              onChange={(e) => setGuestEmail(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.email}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phone Number <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="tel"
                              placeholder="Phone Number"
                              value={guestPhone}
                              onChange={(e) => setGuestPhone(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.phone}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.phone ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <p className="text-xs text-gray-500">
                            Continue to complete your registration and payment securely via Razorpay.
                          </p>
                          <Button type="submit" variant="primary" className="w-full">
                            Continue to Registration
                          </Button>
                        </form>
                      ) : needsExtendedForm(selectedEvent) ? (
                        <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister(selectedEvent.id) } : handleGuestRegister} className="flex-1 space-y-3">
                          {/* Name */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              placeholder="Your Name"
                              value={guestName}
                              onChange={(e) => setGuestName(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.name}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>

                          {/* Email */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                            <input
                              type="email"
                              placeholder="Email Address"
                              value={guestEmail}
                              onChange={(e) => setGuestEmail(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.email}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>

                          {/* Phone with Country Code */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                            <div className="flex gap-2">
                              <select
                                value={guestCountryCode}
                                onChange={(e) => setGuestCountryCode(e.target.value)}
                                className="px-2 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white min-w-[90px]"
                              >
                                {COUNTRY_CODES.map(cc => (
                                  <option key={cc.code} value={cc.code}>{cc.label}</option>
                                ))}
                              </select>
                              <input
                                type="tel"
                                placeholder="Phone Number"
                                value={guestPhone}
                                onChange={(e) => setGuestPhone(e.target.value)}
                                required
                                className="flex-1 px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              />
                            </div>
                          </div>

                          {/* Job Title */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title / Designation</label>
                            <input
                              type="text"
                              placeholder="e.g. Product Manager"
                              value={guestJobTitle}
                              onChange={(e) => setGuestJobTitle(e.target.value)}
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>

                          {/* Organization */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Organization / Company Name</label>
                            <input
                              type="text"
                              placeholder="e.g. Acme Corp"
                              value={guestOrganization}
                              onChange={(e) => setGuestOrganization(e.target.value)}
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>

                          {/* Primary Goal - Multi-select checkboxes */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">What is your primary goal for attending the Breathe Right workshop?</label>
                            <div className="space-y-2">
                              {PRIMARY_GOAL_OPTIONS.map((option) => (
                                <label key={option} className="flex items-start gap-2.5 cursor-pointer min-h-[44px] py-1">
                                  <input
                                    type="checkbox"
                                    checked={guestPrimaryGoals.includes(option)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setGuestPrimaryGoals(prev => [...prev, option])
                                      } else {
                                        setGuestPrimaryGoals(prev => prev.filter(g => g !== option))
                                      }
                                    }}
                                    className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300 flex-shrink-0"
                                  />
                                  <span className="text-sm text-gray-700">{option}</span>
                                </label>
                              ))}
                              <label className="flex items-start gap-2.5 cursor-pointer min-h-[44px] py-1">
                                <input
                                  type="checkbox"
                                  checked={guestPrimaryGoals.includes('Other')}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setGuestPrimaryGoals(prev => [...prev, 'Other'])
                                    } else {
                                      setGuestPrimaryGoals(prev => prev.filter(g => g !== 'Other'))
                                      setGuestOtherGoal('')
                                    }
                                  }}
                                  className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300 flex-shrink-0"
                                />
                                <span className="text-sm text-gray-700">Other</span>
                              </label>
                              {guestPrimaryGoals.includes('Other') && (
                                <input
                                  type="text"
                                  placeholder="Please specify..."
                                  value={guestOtherGoal}
                                  onChange={(e) => setGuestOtherGoal(e.target.value)}
                                  className="w-full ml-6 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              )}
                            </div>
                          </div>

                          {/* How did you hear about us - Single-select radio */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">How did you hear about this workshop? <span className="text-red-500">*</span></label>
                            <div className="space-y-2">
                              {HEARD_ABOUT_OPTIONS.map((option) => (
                                <label key={option} className="flex items-center gap-2.5 cursor-pointer min-h-[44px] py-1">
                                  <input
                                    type="radio"
                                    name="heardAbout"
                                    value={option}
                                    checked={guestHeardAbout === option}
                                    onChange={(e) => { setGuestHeardAbout(e.target.value); setGuestOtherHeardAbout('') }}
                                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                                  />
                                  <span className="text-sm text-gray-700">{option}</span>
                                </label>
                              ))}
                              <label className="flex items-center gap-2.5 cursor-pointer min-h-[44px] py-1">
                                <input
                                  type="radio"
                                  name="heardAbout"
                                  value="Other"
                                  checked={guestHeardAbout === 'Other'}
                                  onChange={(e) => setGuestHeardAbout(e.target.value)}
                                  className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                                />
                                <span className="text-sm text-gray-700">Other</span>
                              </label>
                              {guestHeardAbout === 'Other' && (
                                <input
                                  type="text"
                                  placeholder="Please specify..."
                                  value={guestOtherHeardAbout}
                                  onChange={(e) => setGuestOtherHeardAbout(e.target.value)}
                                  className="w-full ml-6 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              )}
                            </div>
                          </div>

                          <div className="rounded-xl border border-primary-200 bg-white p-3">
                            <p className="text-sm font-medium text-gray-800 mb-2">
                              {EXTENDED_FORM_PAYMENT_TEXT}
                            </p>
                            <img
                              src="/payment-qr-code.png"
                              alt="Payment QR code for Rs. 99 transfer"
                              className="w-full max-w-[260px] mx-auto rounded-lg border border-gray-200"
                              loading="lazy"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Payment Transaction ID <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              placeholder="Enter your UPI transaction ID"
                              value={guestTransactionId}
                              onChange={(e) => setGuestTransactionId(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>

                          <Button
                            type="submit"
                            variant="primary"
                            className="w-full"
                            disabled={registering || (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)}
                          >
                            {registering ? 'Processing...' :
                              (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)
                                ? 'Event Full'
                                : 'Register Now'}
                          </Button>
                        </form>
                      ) : needsExtendedForm2(selectedEvent) ? (
                        <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister(selectedEvent.id) } : handleGuestRegister} className="flex-1 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              placeholder="Your Name"
                              value={guestName}
                              onChange={(e) => setGuestName(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.name}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                            <input
                              type="email"
                              placeholder="Email Address"
                              value={guestEmail}
                              onChange={(e) => setGuestEmail(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.email}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                            <input
                              type="tel"
                              placeholder="Phone Number"
                              value={guestPhone}
                              onChange={(e) => setGuestPhone(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <ExtendedForm2PaymentSection
                            fee={selectedEvent.fee}
                            transactionId={guestTransactionId}
                            onTransactionIdChange={setGuestTransactionId}
                            disabled={registering}
                          />
                          <Button
                            type="submit"
                            variant="primary"
                            className="w-full"
                            disabled={registering || (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)}
                          >
                            {registering ? 'Processing...' :
                              (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)
                                ? 'Event Full'
                                : 'Register Now'}
                          </Button>
                        </form>
                      ) : needsFamilyIntakeForm(selectedEvent) ? (
                        <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister(selectedEvent.id) } : handleGuestRegister} className="flex-1 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              placeholder="Your full name"
                              value={guestName}
                              onChange={(e) => setGuestName(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.name}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phone number (WhatsApp preferred) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="tel"
                              placeholder="Phone / WhatsApp"
                              value={guestPhone}
                              onChange={(e) => setGuestPhone(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email address <span className="text-red-500">*</span></label>
                            <input
                              type="email"
                              placeholder="Email address"
                              value={guestEmail}
                              onChange={(e) => setGuestEmail(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.email}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <p className="block text-sm font-medium text-gray-700 mb-2">Your child&apos;s gender <span className="text-red-500">*</span></p>
                            <div className="space-y-2">
                              <label className="flex items-center gap-2.5 cursor-pointer min-h-[44px] py-1">
                                <input
                                  type="radio"
                                  name="familyIntakeChildGenderEvents"
                                  value="Male"
                                  checked={guestChildGender === 'Male'}
                                  onChange={() => { setGuestChildGender('Male'); setGuestChildGenderOther('') }}
                                  className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                                />
                                <span className="text-sm text-gray-700">Male</span>
                              </label>
                              <label className="flex items-center gap-2.5 cursor-pointer min-h-[44px] py-1">
                                <input
                                  type="radio"
                                  name="familyIntakeChildGenderEvents"
                                  value="Other"
                                  checked={guestChildGender === 'Other'}
                                  onChange={() => setGuestChildGender('Other')}
                                  className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                                />
                                <span className="text-sm text-gray-700">Other</span>
                              </label>
                              {guestChildGender === 'Other' && (
                                <input
                                  type="text"
                                  placeholder="Please specify"
                                  value={guestChildGenderOther}
                                  onChange={(e) => setGuestChildGenderOther(e.target.value)}
                                  className="w-full sm:ml-6 px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Your child&apos;s age <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="Age"
                              value={guestChildAge}
                              onChange={(e) => setGuestChildAge(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Can you tell us a little about yourself. Please feel free to share as little and as much as you like. <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              rows={4}
                              value={guestAboutYourself}
                              onChange={(e) => setGuestAboutYourself(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[100px]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">What do you hope to get out of this session? <span className="text-red-500">*</span></label>
                            <textarea
                              rows={3}
                              value={guestSessionHope}
                              onChange={(e) => setGuestSessionHope(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[88px]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">If you have any questions, you can share them here. <span className="text-red-500">*</span></label>
                            <textarea
                              rows={3}
                              value={guestSharedQuestions}
                              onChange={(e) => setGuestSharedQuestions(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[88px]"
                            />
                          </div>
                          <Button
                            type="submit"
                            variant="primary"
                            className="w-full"
                            disabled={registering || (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)}
                          >
                            {registering ? 'Processing...' :
                              (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)
                                ? 'Event Full'
                                : 'Register Now'}
                          </Button>
                        </form>
                      ) : needsUnmaskingHalfdayApr2026Form(selectedEvent) ? (
                        <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister(selectedEvent.id) } : handleGuestRegister} className="flex-1 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              placeholder="Your name"
                              value={guestName}
                              onChange={(e) => setGuestName(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.name}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                            <input
                              type="email"
                              placeholder="Email"
                              value={guestEmail}
                              onChange={(e) => setGuestEmail(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.email}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                            <input
                              type="tel"
                              placeholder="Phone"
                              value={guestPhone}
                              onChange={(e) => setGuestPhone(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Age <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="Age"
                              value={guestAge}
                              onChange={(e) => setGuestAge(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Gender <span className="text-red-500">*</span></label>
                            <select
                              value={guestGender}
                              onChange={(e) => { setGuestGender(e.target.value); if (e.target.value !== 'Other') setGuestGenderOther('') }}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                            >
                              <option value="">Select…</option>
                              {UNMASKING_GENDER_OPTIONS.map((g) => (
                                <option key={g} value={g}>{g}</option>
                              ))}
                            </select>
                            {guestGender === 'Other' && (
                              <input
                                type="text"
                                placeholder="Please specify"
                                value={guestGenderOther}
                                onChange={(e) => setGuestGenderOther(e.target.value)}
                                className="w-full mt-2 px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Company <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              placeholder="Company or organization"
                              value={guestCompany}
                              onChange={(e) => setGuestCompany(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <p className="block text-sm font-medium text-gray-700 mb-2">Session selection <span className="text-red-500">*</span></p>
                            <div className="space-y-2">
                              {UNMASKING_SESSION_OPTIONS.map((opt) => (
                                <label key={opt.value} className="flex items-start gap-2.5 cursor-pointer min-h-[44px] py-1">
                                  <input
                                    type="radio"
                                    name="unmaskingSessionEvents"
                                    value={opt.value}
                                    checked={guestSessionSelection === opt.value}
                                    onChange={() => setGuestSessionSelection(opt.value)}
                                    className="mt-1 w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 flex-shrink-0"
                                  />
                                  <span className="text-sm text-gray-700">
                                    <span className="font-medium">{opt.value}</span>
                                    <span className="block text-gray-500 text-xs mt-0.5">{opt.subtitle}</span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <ExtendedForm2PaymentSection
                            fee={selectedEvent.fee}
                            transactionId={guestTransactionId}
                            onTransactionIdChange={setGuestTransactionId}
                            disabled={registering}
                          />
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Questions for facilitators or organizers</label>
                            <textarea
                              rows={3}
                              placeholder="Optional"
                              value={guestFacilitatorQueries}
                              onChange={(e) => setGuestFacilitatorQueries(e.target.value)}
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[88px]"
                            />
                          </div>
                          <Button
                            type="submit"
                            variant="primary"
                            className="w-full"
                            disabled={registering || (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)}
                          >
                            {registering ? 'Processing...' :
                              (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)
                                ? 'Event Full'
                                : 'Register Now'}
                          </Button>
                        </form>
                      ) : needsAWayToMoveForm(selectedEvent) ? (
                        <form onSubmit={isLoggedIn ? (e) => { e.preventDefault(); handleRegister(selectedEvent.id) } : handleGuestRegister} className="flex-1 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              placeholder="Your name"
                              value={guestName}
                              onChange={(e) => setGuestName(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.name}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.name ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                            <input
                              type="email"
                              placeholder="Email"
                              value={guestEmail}
                              onChange={(e) => setGuestEmail(e.target.value)}
                              required
                              disabled={isLoggedIn && !!currentUser?.email}
                              className={`w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isLoggedIn && currentUser?.email ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                            <input
                              type="tel"
                              placeholder="Phone"
                              value={guestPhone}
                              onChange={(e) => setGuestPhone(e.target.value)}
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <AWayToMoveSlotPicker
                            name="aWayToMoveSlotEvents"
                            selectedSlot={guestSlotSelection}
                            onChange={setGuestSlotSelection}
                            disabled={registering}
                          />
                          <Button
                            type="submit"
                            variant="primary"
                            className="w-full"
                            disabled={registering || getAvailableAWayToMoveSlotOptions().length === 0}
                          >
                            {registering ? 'Processing...' : 'Enquire Now'}
                          </Button>
                        </form>
                      ) : isLoggedIn ? (
                        <Button 
                          variant="primary" 
                          className="flex-1"
                          onClick={() => handleRegister(selectedEvent.id)}
                          disabled={registering || (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)}
                        >
                          {registering ? 'Processing...' : 
                            (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees) 
                              ? 'Event Full' 
                              : 'Enquire Now'}
                        </Button>
                      ) : (
                        <form onSubmit={handleGuestRegister} className="flex-1 space-y-2.5">
                          <input
                            type="text"
                            placeholder="Your Name"
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            required
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <input
                            type="email"
                            placeholder="Email Address"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            required
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <input
                            type="tel"
                            placeholder="Phone Number"
                            value={guestPhone}
                            onChange={(e) => setGuestPhone(e.target.value)}
                            required
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <Button
                            type="submit"
                            variant="primary"
                            className="w-full"
                            disabled={registering || (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)}
                          >
                            {registering ? 'Processing...' :
                              (selectedEvent.maxAttendees && selectedEvent.registrationCount >= selectedEvent.maxAttendees)
                                ? 'Event Full'
                                : 'Enquire Now'}
                          </Button>
                        </form>
                      )
                    )}

                    {/* External Registration */}
                    {selectedEvent.registrationLink && (
                      <a 
                        href={selectedEvent.registrationLink}
                        target={selectedEvent.registrationLink.startsWith('tel:') ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button variant="outline" className="w-full">
                          External Registration →
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Events
